/**
 * "Save Cart" in the cart header (snippets/normal-cart-header.liquid) and the
 * sales rep's draft order summary (snippets/draft-order-cart-summary.liquid):
 * puts the whole cart into a brand new saved list.
 *
 * COB2B-798 #4: this reuses the "Save To List" drawer shell/rows/new-list-form
 * markup and CSS from addToListLogin.js (scss/components/_login-prompt-modal.scss)
 * rather than a bespoke centered modal — same slide-in drawer, same product-row
 * styling, same "Enter New List Name" form, just listing every cart item instead
 * of the one product a PDP/PLP "add to list" click carries. A cart saved here is
 * a list the Your Lists page can open, edit and push back into the cart.
 *
 * Strings come from window.saveCartPrompt (theme.liquid).
 */

import {
  closeModal,
  fetchSavedLists,
  isCurrentOverlay,
  mountOverlay,
  openLoginModal,
  primeSavedLists,
  setModalBody,
  setMessageTone,
} from './addToListLogin';
import {
  findFreeSlotIndex,
  generateListId,
  metafieldKeyFromIndex,
  putOrderList,
} from '../objects/your-lists/orderListApi';

/**
 * The cart as list items: { id (variant id), title, quantity, selected } — the
 * shape YourListDetail reads, plus `image` (display only, not persisted) for the
 * drawer's product rows. Read from /cart.js rather than the rendered rows so a
 * quantity changed a moment ago is included, and marked selected because the
 * whole cart is what's being saved.
 *
 * COB2B-798 #4: `item.id` on a /cart.js line is the line key, not the variant
 * id — they only happen to match when the line has no properties. Every other
 * place in this app reading a variant id off a /cart.js line uses
 * `item.variant_id` instead (e.g. js/objects/cart/draft_cart.js's own
 * `variantId: gid://shopify/ProductVariant/${item.variant_id}` when building
 * the draft order). Using `item.id` here saved the wrong id into the list, so
 * fetchListProducts.js's Storefront lookup found no matching product —
 * "Unavailable Products" / no image, name only (from the stored title).
 */
async function fetchCartItems() {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`, {
    headers: { 'X-Requested-With': 'fetch' },
  });
  if (!response.ok) throw new Error(`cart.js HTTP ${response.status}`);

  const cart = await response.json();
  return (cart.items || []).map((item) => ({
    id: String(item.variant_id),
    title: item.product_title || item.title || '',
    image: item.image || '',
    quantity: item.quantity,
    selected: true,
  }));
}

/** Escape text dropped into an innerHTML template. */
function escapeHtml(value) {
  return String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        ch
      ],
  );
}

/** One cart item row — same shape as addToListLogin's single product header. */
function productRowHtml(item) {
  const qtyLabel = item.quantity > 1 ? ` &times; ${item.quantity}` : '';
  return `
    <div class="mr-list-modal-product">
      ${item.image ? `<img class="mr-list-modal-product-image" src="${escapeHtml(item.image)}" alt="" aria-hidden="true">` : ''}
      <div class="mr-list-modal-product-title">${escapeHtml(item.title)}${qtyLabel}</div>
    </div>`;
}

/** The "Enter New List Name" form — same markup as addToListLogin's inline create-list form. */
function newlistFormHtml(p) {
  return `
    <div class="mr-list-modal-newlist">
      <div class="mr-list-modal-newlist-label">${p.enterListName || 'Enter New List Name'}</div>
      <div class="mr-list-modal-newlist-row">
        <input
          type="text"
          class="mr-list-modal-input mr-list-modal-newlist-input"
          maxlength="60"
          placeholder="${p.placeholder || 'e.g. Weekly reorder'}"
          aria-label="${p.nameLabel || 'List name'}"
        >
        <button type="button" class="mr-list-modal-save mr-list-modal-newlist-save" disabled>${p.saveNewList || 'Save New List'}</button>
      </div>
      <div class="mr-list-modal-message mr-list-modal-newlist-message" role="status" aria-live="polite"></div>
    </div>`;
}

/** Fill the (already open) drawer with the cart's rows + the new-list-name form. */
function renderCartItemsForm(overlay, p, items) {
  if (items.length === 0) {
    setModalBody(
      overlay,
      `<div class="mr-list-modal-message is-error">${p.emptyCart || 'Your cart is empty.'}</div>
       <div class="mr-list-modal-actions">
         <button type="button" class="mr-list-modal-cancel">${p.cancel || 'Cancel'}</button>
       </div>`,
    );
    overlay
      .querySelector('.mr-list-modal-cancel')
      .addEventListener('click', closeModal);
    return;
  }

  setModalBody(
    overlay,
    `<div class="mr-list-modal-rows">
       ${items.map(productRowHtml).join('')}
     </div>
     <div class="mr-list-modal-newlist-slot">
       ${newlistFormHtml(p)}
     </div>`,
  );

  const input = overlay.querySelector('.mr-list-modal-newlist-input');
  const saveBtn = overlay.querySelector('.mr-list-modal-newlist-save');
  const message = overlay.querySelector('.mr-list-modal-newlist-message');

  input.focus();
  input.addEventListener('input', () => {
    saveBtn.disabled = !input.value.trim();
    setMessageTone(message, '');
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !saveBtn.disabled) saveBtn.click();
  });

  saveBtn.addEventListener('click', async () => {
    const name = input.value.trim();
    if (!name || saveBtn.disabled) return;

    saveBtn.disabled = true;
    input.disabled = true;
    saveBtn.textContent = p.saving || 'Saving…';
    setMessageTone(message, '');

    const fail = (text) => {
      if (!isCurrentOverlay(overlay)) return;
      saveBtn.disabled = false;
      input.disabled = false;
      saveBtn.textContent = p.saveNewList || 'Save New List';
      setMessageTone(message, text, 'error');
    };

    try {
      // The lists are needed for the slot, not for display: a list is its own
      // company metafield (order_list_1..30), so creating one means claiming the
      // lowest free slot — the same thing the Your Lists page does. Fresh from
      // the server: a slot that only looks free would overwrite a list (COB2B-785).
      const lists = await fetchSavedLists({ fresh: true });

      const freeIndex = findFreeSlotIndex(lists);
      if (freeIndex === null) {
        fail(p.limitReached || 'You have reached the maximum number of lists.');
        return;
      }

      const list = {
        id: generateListId(),
        name,
        items: items.map(({ id, title, quantity, selected }) => ({
          id,
          title,
          quantity,
          selected,
        })),
        createdAt: new Date().toISOString(),
      };

      await putOrderList(freeIndex, list);

      // Keep the add-to-list picker's cache honest — it's the same session cache.
      primeSavedLists([
        ...lists,
        { ...list, metafieldKey: metafieldKeyFromIndex(freeIndex) },
      ]);

      if (!isCurrentOverlay(overlay)) return;
      setModalBody(
        overlay,
        `<div class="mr-list-modal-message is-success">${p.success || 'Your cart has been saved.'}</div>
         <div class="mr-list-modal-actions">
           <a class="mr-list-modal-save" href="${p.yourListsUrl || '/pages/your-lists'}">${p.viewLists || 'View my lists'}</a>
           <button type="button" class="mr-list-modal-cancel">${p.done || 'Done'}</button>
         </div>`,
      );
      overlay
        .querySelector('.mr-list-modal-cancel')
        .addEventListener('click', closeModal);
    } catch (error) {
      console.error('Save cart to list failed', error);
      fail(p.error || 'Something went wrong. Please try again.');
    }
  });
}

function openSaveCartDrawer() {
  const p = window.saveCartPrompt || {};

  // Same drawer shell + close chevron as addToListLogin's openListModal, opened
  // with a loading state then filled in once /cart.js resolves.
  const overlay = mountOverlay(
    'mr-list-drawer-overlay',
    `
    <div class="mr-list-modal" role="dialog" aria-modal="true">
      <button type="button" class="mr-login-modal-close" aria-label="${p.cancel || 'Cancel'}">
        <svg width="28" height="11" viewBox="0 0 28 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.31661e-07 4.93763L25.8569 4.93763L21.7327 0.795195L22.5246 4.78674e-07L28 5.5L22.5246 11L21.7327 10.2048L25.8569 6.06237L5.29989e-07 6.06238L4.31661e-07 4.93763Z" fill="black"/>
        </svg>
      </button>
      <div class="mr-list-modal-title">${p.title || 'Save cart as a list'}</div>
      <div class="mr-list-modal-body">
        <div class="mr-list-modal-loading">${p.loading || 'Loading…'}</div>
      </div>
    </div>`,
  );
  if (!overlay) return;

  overlay
    .querySelector('.mr-login-modal-close')
    .addEventListener('click', closeModal);

  fetchCartItems()
    .then((items) => {
      if (!isCurrentOverlay(overlay)) return;
      renderCartItemsForm(overlay, p, items);
    })
    .catch((error) => {
      console.error('Save cart to list failed', error);
      if (!isCurrentOverlay(overlay)) return;
      setModalBody(
        overlay,
        `<div class="mr-list-modal-message is-error">${p.error || 'Something went wrong. Please try again.'}</div>
         <div class="mr-list-modal-actions">
           <button type="button" class="mr-list-modal-cancel">${p.cancel || 'Cancel'}</button>
         </div>`,
      );
      overlay
        .querySelector('.mr-list-modal-cancel')
        .addEventListener('click', closeModal);
    });
}

const saveCartToList = () => {
  // Delegated: the cart header is re-rendered on every cart update (see
  // renderSections in assets/cart.js), so the button is not the same element for
  // long.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-save-cart]');
    if (!button) return;

    event.preventDefault();
    if (window.customerLoggedIn) openSaveCartDrawer();
    else openLoginModal();
  });
};

export default saveCartToList;
